import React, { useState } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { colors } from "../../theme/colors";
import CustomTruncateText from "../CustomTruncateText";

export interface CustomFileNameDisplayProps {
  fileName: string;
  fontSize?: number;
  fontWeight?: number;
  color?: string;
  ellipsis?: string;
}

const formatFileName = (
  fileName: string
): { name: string; extension: string } => {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex < 1) {
    return { name: fileName, extension: "" };
  }
  const name = fileName.substring(0, lastDotIndex);
  const extension = fileName.substring(lastDotIndex + 1).toLowerCase();
  return { name, extension };
};

export const CustomFileNameDisplay: React.FC<CustomFileNameDisplayProps> = ({
  fileName,
  fontSize = 14,
  fontWeight = 700,
  color = colors.text.primary,
  ellipsis = "...",
}) => {
  const { name, extension } = formatFileName(fileName);
  const [isTruncated, setIsTruncated] = useState(false);

  return (
    <Tooltip
      title={fileName}
      placement="top"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: "rgba(62, 62, 62, 0.95)",
            color: "#fff",
            fontSize: 12,
            fontWeight: 400,
            padding: "6px 12px",
            borderRadius: "4px",
            maxWidth: 300,
            wordBreak: "break-all",
          },
        },
        arrow: {
          sx: {
            color: "rgba(62, 62, 62, 0.95)",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 0,
          width: "100%",
          overflow: "hidden",
          gap: 0,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            flexShrink: 1,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            component="div"
            sx={{
              fontSize,
              color,
              fontWeight,
              lineHeight: 1.4,
              fontFamily: "'Noto Sans JP', sans-serif",
              minWidth: 0,
              overflow: "hidden",
              "& > span": {
                lineHeight: 1.4,
              },
            }}
          >
            <CustomTruncateText
              text={name}
              ellipsis={ellipsis}
              onTruncated={setIsTruncated}
            />
          </Typography>
        </Box>
        {extension && (
          <Typography
            component="span"
            sx={{
              fontSize,
              color,
              fontWeight,
              lineHeight: 1.4,
              fontFamily: "'Noto Sans JP', sans-serif",
              flexShrink: 0,
              whiteSpace: "nowrap",
              marginLeft: isTruncated ? 0.5 : 0,
            }}
          >
            .{extension}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};
